import { computed, ref } from 'vue'

import {
  useCampaignService,
  type ICampaignKnowledgeDocument,
} from '@/services/campaign/useCampaignService'

export const KNOWLEDGE_STATUS_COLOR_MAP = {
  ready: 'success',
  pending: 'warning',
  indexing: 'info',
  failed: 'danger',
  disabled: 'secondary',
} as const

export const KNOWLEDGE_STATUS_LABEL_MAP = {
  ready: 'Lista',
  pending: 'Pendiente',
  indexing: 'Procesando',
  failed: 'Fallida',
  disabled: 'Deshabilitada',
}

export const KNOWLEDGE_PROCESSING_STATUSES = ['pending', 'indexing']

export const useCampaignKnowledge = () => {
  const campaignService = useCampaignService()
  const documents = ref<ICampaignKnowledgeDocument[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const loadedCampaignId = ref<string | null>(null)

  const readyDocuments = computed(() =>
    documents.value.filter((document) => document.active && document.status === 'ready'),
  )

  const cardBody = computed(() => {
    if (loading.value) return ['Cargando base de conocimiento...']
    if (error.value) return [error.value]
    if (!documents.value.length) return ['No hay bases de conocimiento cargadas.']

    return documents.value.map((document) => {
      const status = document.status ? ` (${document.status})` : ''
      return `${document.title}${status}`
    })
  })

  const loadDocuments = async (campaignId?: string | null) => {
    if (!campaignId) {
      reset()
      return
    }

    if (loadedCampaignId.value === campaignId) return

    loading.value = true
    error.value = null
    const requestCampaignId = campaignId

    try {
      const response = await campaignService.listCampaignKnowledgeDocuments(campaignId)
      if (loadedCampaignId.value && loadedCampaignId.value !== requestCampaignId) return
      documents.value = response.items ?? []
      loadedCampaignId.value = requestCampaignId
    } catch {
      documents.value = []
      loadedCampaignId.value = null
      error.value = 'No se pudo cargar la base de conocimiento.'
    } finally {
      if (!loadedCampaignId.value || loadedCampaignId.value === requestCampaignId) {
        loading.value = false
      }
    }
  }

  const reset = () => {
    documents.value = []
    loading.value = false
    error.value = null
    loadedCampaignId.value = null
  }

  return {
    cardBody,
    documents,
    error,
    loadDocuments,
    loading,
    readyDocuments,
    reset,
  }
}
